import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { BrandIcon } from "@components/Icons/BrandIcon";
import { Loading } from "@components/Loading";
import { TopNav } from "@components/TopNav";
import { FirebaseContext } from "@helpers/firebase";
import { useBranding } from "@helpers/useBranding";
import { useTranslation } from "@helpers/useTranslation";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { useNavigate } from "react-router-dom";

export const MyEvents = () => {
  const { t } = useTranslation("admin");
  const { functions } = useContext(FirebaseContext);
  const [getMyEvents, loading] = useHttpsCallable<API.GetMyEventsRequest, API.GetMyEventsResponse>(
    functions,
    "getMyEventsV2",
  );

  const [myEvents, setMyEvents] = useState<MyEvent[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { appDomain } = useBranding();

  // Helper function to check if an event is today
  const isEventToday = (eventDate: string) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0]; // Get YYYY-MM-DD format

    // Try to parse the event date - it might be in different formats
    const eventDateObj = new Date(eventDate);
    const eventDateStr = eventDateObj.toISOString().split("T")[0];

    return todayStr === eventDateStr;
  };

  const filteredEvents = useMemo(() => {
    return myEvents
      .filter((event) => event.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [myEvents, searchQuery]);

  const useCompactView = filteredEvents.length > 5;

  useEffect(() => {
    getMyEvents().then((result) => {
      setMyEvents(result.data);
    });
  }, []);

  const navigate = useNavigate();

  const eventsExist = myEvents.length > 0;
  return (
    <>
      <TopNav isAdmin={true} useAllLocales hideUpgrade hidePreview />
      <main className="flex-1 flex-col">
        <div className="mb-24 flex flex-col items-center py-4 px-6">
          <Loading loading={loading}>
            <div className="flex w-full max-w-lg flex-col">
              <h1 className="mt-12 mb-8 text-center text-4xl font-semibold">{t("myEvents")}</h1>
              <p className="mb-6 text-center text-lg font-light">{t("aboutMyEvents")}</p>

              {eventsExist && (
                <div className="mb-6">
                  <input
                    type="text"
                    placeholder={t("searchEvents")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-bordered input w-full"
                  />
                </div>
              )}

              {filteredEvents.length === 0 && searchQuery && (
                <div className="text-center text-xl font-semibold">{t(`noEventsFoundMatching '${searchQuery}'`)}</div>
              )}

              {!eventsExist && <div className="text-center text-xl font-semibold">{t("noEventsYet")}</div>}

              {filteredEvents.map((event) => {
                const isTodayEvent = isEventToday(event.date);
                const compactClasses = useCompactView ? "mb-4 py-1" : "mb-8 py-2";
                const todayClasses = isTodayEvent
                  ? "bg-green-100 border-green-300 ring-green-200"
                  : "bg-base-200 border-base-300";

                return (
                  <a
                    className="group hover:cursor-pointer"
                    onClick={() => navigate(`/${event.id}/admin`)}
                    key={event.id}
                  >
                    <div
                      className={`card card-side ${compactClasses} rounded-2xl border ${todayClasses} shadow-xl`}
                      key={event.id}
                    >
                      <div className="avatar ml-4 items-center">
                        <div
                          className={`${useCompactView ? "h-16 w-16" : "h-24 w-24"
                            } rounded-full opacity-90 ring ring-base-300 ring-offset-2 ring-offset-base-100 group-hover:opacity-100 group-hover:ring-primary`}
                        >
                          <FirebaseCachedImage
                            uri={`events/${event.id}/background_light`}
                            defaultUri={
                              event?.type === "wedding" ? "bg-wedding.webp_light" : "bg-conference.webp_light"
                            }
                            alt=""
                          />
                        </div>
                      </div>

                      <div
                        className={`overflow-ios-fix card-body overflow-x-scroll ${useCompactView ? "py-2" : "py-4"}`}
                      >
                        <h1 className={`card-title mr-4 max-h-32 pr-4 ${useCompactView ? "text-lg" : ""}`}>
                          {event.name}
                        </h1>
                        <div className={`max-h-32 ${useCompactView ? "mt-1 text-sm" : "mt-2"}`}>
                          {appDomain}/{event.id}
                        </div>
                        <div
                          className={`max-h-32 text-sm font-light text-neutral text-opacity-70 ${isTodayEvent ? "" : "font-light"
                            }`}
                        >
                          {event.date} {isTodayEvent && `(${t("today")})`}
                        </div>
                      </div>

                      <span
                        className="pointer-events-none absolute right-6 top-6 text-gray-200 text-gray-300 group-hover:text-gray-700"
                        aria-hidden="true"
                      >
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                        </svg>
                      </span>
                    </div>
                  </a>
                );
              })}
              <div className="mb-8 flex justify-center">
                <button
                  onClick={() => navigate("/addevent")}
                  className="btn-outline btn-primary btn my-6 h-10 w-28 text-lg"
                  type="button"
                >
                  +
                </button>
              </div>
              <div className="mb-12 flex justify-center">
                <button className={`btn-link btn flex flex-row `} onClick={() => navigate("/branding")}>
                  <BrandIcon />
                  <span>{t("myBranding")}</span>
                </button>
              </div>
            </div>
          </Loading>
        </div>
      </main>
    </>
  );
};
